import React from 'react';
import { ThemeBackgroundDecorations, ThemeDefinition } from './types';

const linearBackgroundDecorations: ThemeBackgroundDecorations = {
  elements: [
    { className: 'linear-top-rail' },
    { className: 'linear-grid' },
    {
      className: 'linear-blob',
      style: {
        top: '10%',
        right: '5%',
        width: '40vw',
        height: '40vw',
        background: 'var(--linear-decor-blob-primary)',
        animationDelay: '0s',
      },
    },
    {
      className: 'linear-blob',
      style: {
        bottom: '10%',
        left: '5%',
        width: '35vw',
        height: '35vw',
        background: 'var(--linear-decor-blob-secondary)',
        animationDelay: '-5s',
        opacity: 0.1,
      },
    },
  ],
};

export const linearTheme: ThemeDefinition = {
  id: 'linear',
  name: 'Dark Modern',
  description: 'Deep atmosphere, precision depth, and ambient lighting pools.',
  layoutMode: 'top-nav',
  headerPreferences: {
    showLogoDefault: true,
    showWordmarkDefault: true,
  },
  headerBehavior: {
    mobileHorizontalPadding: '4px',
    mobileInsetOffset: 'var(--shell-gap)',
  },
  sidebarBehavior: {
    compactHeightScrollFooter: false,
    zeroDesktopPanelPadding: false,
    navButtonGap: '2px',
    scrollerPaddingBottom: '0px',
    listPaddingBottom: '0px',
    itemPaddingBottom: '0px',
    hideStorageFooterOnMobile: false,
    mobileDrawerDocked: true,
    mobileDrawerBorderRadius: 'var(--radius-ui) var(--radius-ui) 0 0',
    mobileDrawerMarginTop: 'auto',
    mobileDrawerMarginBottom: '0px',
    mobileDrawerMaxHeight: '65vh',
    mobileDrawerWidth: '100%',
    mobileDrawerTop: 'auto',
    mobileDrawerLeft: '0',
    mobileDrawerRight: '0',
    mobileDrawerBottom: '0',
  },
  backgroundDecorations: linearBackgroundDecorations,
  layout: {
    desktop: {
      navPlacement: 'top',
      headerFrameMode: 'flush',
      mobileHeaderInset: false,
      showHeaderToggleOnMobile: false,
      showDesktopNavItems: true,
      showStorageHeaderWidget: true,
      headerVersionPlacement: 'desktop',
      headerTitleInset: '12px',
      headerToggleMode: 'menu',
      headerToggleWidth: '44px',
      headerToggleHeight: '44px',
      headerToggleRadius: 'var(--radius-ui)',
      headerToggleColor: '#FFFFFF',
      headerUpdateIndicatorMode: 'linear',
      headerBackground: '#050506',
      headerBorder: 'none',
      headerBorderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      headerPattern: 'none',
      headerBackdropFilter: 'none',
      headerBorderRadius: '0px',
      headerMenuRadius: '12px',
      headerMenuBorder: '1px solid rgba(255, 255, 255, 0.1)',
      headerMenuBackground: '#09090b',
      headerMenuShadow: '0 10px 40px rgba(0,0,0,0.5)',
      shellBackground: '#050506',
      mainPadding: '16px 16px 24px',
      mainMarginTop: '64px',
      contentPadding: '12px 16px',
      contentMaxWidth: '1400px',
      contentMarginInline: 'auto',
      contentFrameBackground: 'transparent',
      contentFrameBorder: 'none',
      contentFrameRadius: '0px',
      contentFrameShadow: 'none',
    },
    mobile: {
      navPlacement: 'top',
      headerFrameMode: 'flush',
      mobileHeaderInset: false,
      showHeaderToggleOnMobile: false,
      showDesktopNavItems: false,
      showStorageHeaderWidget: true,
      headerVersionPlacement: 'mobile',
      headerTitleInset: '0px',
      headerToggleMode: 'menu',
      headerToggleWidth: '44px',
      headerToggleHeight: '44px',
      headerToggleRadius: 'var(--radius-ui)',
      headerToggleColor: '#FFFFFF',
      headerUpdateIndicatorMode: 'linear',
      headerBackground: '#050506',
      headerBorder: 'none',
      headerBorderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      headerPattern: 'none',
      headerBackdropFilter: 'none',
      headerBorderRadius: '0px',
      headerMenuRadius: '12px',
      headerMenuBorder: '1px solid rgba(255, 255, 255, 0.1)',
      headerMenuBackground: '#09090b',
      headerMenuShadow: '0 10px 40px rgba(0,0,0,0.5)',
      shellBackground: '#050506',
      mainPadding: '8px 4px calc(20px + env(safe-area-inset-bottom))',
      mainMarginTop: '64px',
      contentPadding: '8px 4px',
      contentMaxWidth: '1400px',
      contentMarginInline: 'auto',
      contentFrameBackground: 'transparent',
      contentFrameBorder: 'none',
      contentFrameRadius: '0px',
      contentFrameShadow: 'none',
    },
  },
  preview: React.createElement('div', {
    key: 'preview-root',
    style: {
      padding: '20px',
      borderRadius: '12px',
      backgroundColor: '#050506',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden',
      position: 'relative',
      width: '100%',
      minHeight: 80,
    }
  }, [
    React.createElement('div', {
      key: 'b1',
      style: {
        position: 'absolute',
        top: -10,
        right: -10,
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(94, 106, 210, 0.3) 0%, transparent 70%)',
        filter: 'blur(5px)',
      }
    }),
    React.createElement('div', {
      key: 'b2',
      style: { width: 44, height: 44, borderRadius: '6px', backgroundColor: '#5E6AD2', boxShadow: '0 0 15px rgba(94, 106, 210, 0.4)' }
    }),
    React.createElement('div', {
      key: 'b3',
      style: { width: 90, height: 16, borderRadius: '9999px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }
    })
  ]),
  tokens: {
    light: {
      'font-body': "'IBM Plex Sans'",
      'font-display': "'Space Grotesk'",
      background: '240 10% 3.9%', // Dark by default for linear
      foreground: '0 0% 98%',
      card: '240 10% 3.9%',
      'card-foreground': '0 0% 98%',
      popover: '240 10% 3.9%',
      'popover-foreground': '0 0% 98%',
      primary: '235 56% 59%', // #5E6AD2
      'primary-foreground': '0 0% 100%',
      secondary: '142 72% 45%',
      'secondary-foreground': '0 0% 10%',
      muted: '240 3.7% 15.9%',
      'muted-foreground': '240 5% 64.9%',
      accent: '38 92% 55%',
      'accent-foreground': '0 0% 10%',
      destructive: '0 72% 50%',
      'destructive-foreground': '0 0% 98%',
      'success': '142 60% 45%',
      'success-foreground': '0 0% 98%',
      'warning': '38 90% 55%',
      'warning-foreground': '0 0% 10%',
      'info': '200 85% 55%',
      'info-foreground': '0 0% 10%',
      border: '240 3.7% 15.9%',
      'border-strong': '240 5% 30%',
      input: '240 3.7% 15.9%',
      'input-border': '240 3.7% 15.9%',
      'input-border-hover': '235 56% 59%',
      ring: '235 56% 59%',
      radius: '0.125rem', // 2px
      'radius-ui': '2px',
      'radius-input': '2px',
      'radius-thumb': '2px',
      'border-weight': '1px',
      'nav-radius': '0px',
      'action-bar-gap': '6px',
      'action-bar-gap-compact': '6px',
      'action-bar-padding-y': '8px',
      'action-bar-padding-x': '0px',
      'action-bar-foreground': '0 0% 98%',
      'action-bar-border': '240 3.7% 15.9%',
      'action-bar-hover-border': '240 5% 30%',
      'action-bar-disabled-foreground': '240 5% 64.9%',
      'snackbar-surface-background': 'rgba(17, 24, 39, 0.96)',
      'snackbar-surface-border': 'rgba(255, 255, 255, 0.14)',
      'snackbar-surface-foreground': '#ffffff',
      'overlay-backdrop-background': 'rgba(0, 0, 0, 0.5)',
      'overlay-backdrop-background-strong': 'rgba(0, 0, 0, 0.6)',
      'overlay-backdrop-filter': 'blur(4px)',
      'media-placeholder-background': '#111827',
      'media-placeholder-border': '1px solid rgba(107, 114, 128, 0.4)',
      'media-overlay-foreground': '#ffffff',
      'media-overlay-background': 'rgba(0, 0, 0, 0.6)',
      'media-overlay-background-strong': 'rgba(0, 0, 0, 0.8)',
      'media-overlay-danger-background': 'rgba(211, 47, 47, 0.95)',
      'media-overlay-selection-background': 'rgba(25, 118, 210, 0.2)',
      'media-overlay-delete-selection-background': 'rgba(220, 38, 38, 0.2)',
      'media-overlay-delete-indicator-background': 'rgba(220, 38, 38, 0.8)',
      'media-overlay-ignore-button-background': 'rgba(0, 0, 0, 0.6)',
      'media-overlay-text-shadow': 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5))',
      'video-modal-media-background': 'var(--card)',
      'video-modal-media-border': '1px solid rgba(255, 255, 255, 0.16)',
      'video-modal-media-radius': 'var(--radius-ui)',
      'video-modal-media-max-height-mobile': '42vh',
      'video-modal-media-max-height-desktop': '56vh',
      'video-modal-media-overlay-gradient': 'linear-gradient(to bottom, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.52) 100%)',
      'video-modal-overlay-action-background': 'rgba(0, 0, 0, 0.78)',
      'video-modal-overlay-action-foreground': '#ffffff',
      'video-modal-overlay-action-size': '72px',
      'video-modal-overlay-action-icon-size': '48px',
      'video-modal-overlay-download-size': '90px',
      'video-modal-overlay-download-icon-size': '50px',
      'video-modal-overlay-corner-background': 'rgba(0, 0, 0, 0.64)',
      'video-modal-overlay-corner-foreground': '#ffffff',
      'video-modal-action-row-gap': '8px',
      'video-modal-action-row-padding-y': '2px',
      'video-modal-action-control-height': '28px',
      'video-modal-action-control-min-width': '28px',
      'video-modal-action-control-padding-x': '8px',
      'video-modal-content-gap': '10px',
      'video-modal-content-padding-mobile': '8px',
      'video-modal-content-padding-desktop': '12px',
      'video-modal-action-divider': 'rgba(255, 255, 255, 0.18)',
      'video-modal-section-gap': '12px',
      'video-modal-delete-outline': 'var(--destructive)',
      'video-modal-delete-foreground': 'var(--destructive)',
      'video-modal-link-color': 'hsl(var(--primary))',
      'video-modal-info-bubble-background': 'rgba(18, 18, 20, 0.98)',
      'video-modal-info-bubble-foreground': 'var(--foreground)',
      'video-modal-info-bubble-border': '1px solid rgba(255, 255, 255, 0.12)',
      'video-modal-info-bubble-shadow': '0 8px 24px rgba(0, 0, 0, 0.4)',
      'auth-splash-background': 'linear-gradient(135deg, #09090b 0%, #1a1a1f 100%)',
      'auth-surface-background': 'rgba(18, 18, 20, 0.95)',
      'auth-surface-border': '1px solid rgba(255, 255, 255, 0.1)',
      'auth-surface-shadow': '0 8px 32px rgba(0, 0, 0, 0.5)',
      'auth-surface-backdrop-filter': 'blur(20px)',
      'auth-surface-transform': 'none',
      'auth-surface-padding': '32px',
      'auth-title-font-weight': '700',
      'auth-title-font-size': '2.5rem',
      'auth-title-letter-spacing': '0.02em',
      'auth-title-text-shadow': '0 2px 10px rgba(102, 126, 234, 0.5)',
      'auth-subtitle-font-size': '0.9rem',
      'auth-button-text-transform': 'uppercase',
      'auth-button-letter-spacing': '0.1em',
      'linear-decor-blob-primary': 'radial-gradient(circle, rgba(94, 106, 210, 0.55) 0%, transparent 70%)',
      'linear-decor-blob-secondary': 'radial-gradient(circle, rgba(244, 114, 182, 0.4) 0%, transparent 70%)',
      'linear-decor-top-rail': 'linear-gradient(90deg, transparent, rgba(94, 106, 210, 0.5), transparent)',
      'fab-primary-bg': 'var(--primary)',
      'fab-primary-fg': 'var(--primary-foreground)',
      'fab-primary-hover-bg': 'var(--primary)',
      'fab-primary-hover-fg': 'var(--primary-foreground)',
      'fab-secondary-bg': 'var(--secondary)',
      'fab-secondary-fg': 'var(--secondary-foreground)',
      'fab-secondary-hover-bg': 'var(--secondary)',
      'fab-secondary-hover-fg': 'var(--secondary-foreground)',
      'fab-error-bg': 'var(--destructive)',
      'fab-error-fg': 'var(--destructive-foreground)',
      'fab-error-hover-bg': 'var(--destructive)',
      'fab-error-hover-fg': 'var(--destructive-foreground)',
      'selection-fab-size': '58px',
      'selection-fab-radius': '0px',
      'selection-fab-border': '1px solid rgba(255, 255, 255, 0.12)',
      'selection-fab-shadow': '0 0 0 1px rgba(255, 255, 255, 0.1), 0 18px 38px rgba(0, 0, 0, 0.42)',
      'selection-fab-shadow-hover': '0 0 0 1px rgba(94, 106, 210, 0.35), 0 24px 48px rgba(0, 0, 0, 0.5)',
      'selection-fab-shadow-active': '0 0 0 1px rgba(94, 106, 210, 0.45), 0 14px 24px rgba(0, 0, 0, 0.34)',
      'selection-fab-icon-size': '22px',
      'selection-fab-badge-background': 'rgba(18, 18, 20, 0.96)',
      'selection-fab-badge-foreground': '#ffffff',
      'selection-fab-badge-border': '1px solid rgba(255, 255, 255, 0.14)',
      'selection-fab-badge-shadow': '0 8px 20px rgba(0, 0, 0, 0.32)',
      'selection-fab-badge-radius': '999px',
      'audio-chip-radius': '0px',
      'audio-chip-bg': 'transparent',
      'audio-chip-border': 'rgba(255, 255, 255, 0.4)',
      'audio-chip-foreground': 'var(--foreground)',
      'audio-chip-icon': 'var(--foreground)',
      'audio-chip-shadow': 'none',
      'audio-chip-shadow-hover': 'none',
      'video-chip-radius': '0px',
      'video-chip-bg': 'transparent',
      'video-chip-border': 'rgba(255, 255, 255, 0.25)',
      'video-chip-foreground': 'var(--foreground)',
      'video-chip-icon': 'var(--foreground)',
      'video-chip-shadow': 'none',
      'video-chip-shadow-hover': 'none',
      'audio-control-radius': '0px',
      'audio-control-border': 'rgba(255, 255, 255, 0.4)',
      'audio-control-bg': 'rgba(0, 0, 0, 0.6)',
      'audio-control-foreground': 'var(--foreground)',
      'audio-control-shadow': '0 0 0 1px rgba(255, 255, 255, 0.2)',
      'audio-control-shadow-focus': '0 0 0 1px rgba(255, 255, 255, 0.35)',
      /* Chip & rating shadows */
      'chip-shadow': 'none',
      'chip-shadow-hover': 'var(--shadow-hard-hover)',
      'rating-chip-shadow': 'none',
      'rating-chip-shadow-hover': 'var(--shadow-hard-hover)',
      'status-chip-max-width': '140px',
      'rating-chip-max-width': '120px',
      'nav-hover-style': 'flat-highlight',
      'shadow-soft': '0 8px 32px rgba(0, 0, 0, 0.4)',
      'shadow-hard': '0 12px 48px rgba(0, 0, 0, 0.6)',
      'shadow-hard-hover': '0 16px 64px rgba(0, 0, 0, 0.8)',
      'transition-smooth': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      'nav-border': '1px solid rgba(255, 255, 255, 0.08)',
      'nav-shadow': '0 12px 40px rgba(0, 0, 0, 0.45)',
      'nav-item-border': '1px solid rgba(255, 255, 255, 0.08)',
      'nav-item-border-selected': '1px solid rgba(94, 106, 210, 0.6)',
      'nav-item-border-hover': '1px solid rgba(255, 255, 255, 0.08)',
      'nav-item-bg': 'rgba(255, 255, 255, 0.02)',
      'nav-item-bg-selected': 'rgba(94, 106, 210, 0.2)',
      'nav-item-bg-hover': 'rgba(255, 255, 255, 0.06)',
      'nav-item-shadow': 'none',
      'nav-item-shadow-selected': '0 8px 24px rgba(0, 0, 0, 0.35)',
      'nav-item-shadow-hover': '0 8px 24px rgba(0, 0, 0, 0.25)',
      'header-nav-active-color': 'hsl(var(--primary))',
      'header-nav-default-color': 'var(--muted-foreground)',
      'header-subnav-active-color': 'hsl(var(--primary))',
      'header-update-indicator-width': '32px',
      'header-update-indicator-height': '32px',
      'header-update-indicator-radius': '6px',
      'header-update-indicator-foreground': 'var(--warning)',
      'header-update-indicator-background': 'transparent',
      'header-update-indicator-border': '1px solid rgba(255, 255, 255, 0.2)',
      'header-update-indicator-shadow': 'none',
      'mobile-subnav-surface-background': '#050506',
      'mobile-subnav-surface-border-top': '1px solid rgba(255, 255, 255, 0.1)',
      'mobile-subnav-surface-radius': 'var(--radius-ui)',
      'mobile-subnav-surface-margin-bottom': '0',
      'mobile-subnav-surface-padding-bottom': '8px',
      'mobile-subnav-item-border': '1px solid rgba(255, 255, 255, 0.12)',
      'mobile-subnav-item-border-selected': '1px solid rgba(94, 106, 210, 0.7)',
      'mobile-subnav-item-radius': 'var(--radius-ui)',
      'mobile-subnav-item-text-transform': 'uppercase',
      'mobile-subnav-item-letter-spacing': '0.08em',
      'mobile-primary-nav-surface-background': '#050506',
      'mobile-primary-nav-surface-border-top': '1px solid rgba(255, 255, 255, 0.1)',
      'mobile-primary-nav-surface-radius': '0',
      'mobile-primary-nav-surface-shadow': '0 -12px 30px rgba(0, 0, 0, 0.45)',
      'mobile-primary-nav-active-color': '#ffffff',
      'mobile-primary-nav-active-background': 'rgba(94, 106, 210, 0.16)',
      'mobile-primary-nav-label-font-size': '0.62rem',
      'mobile-primary-nav-label-text-transform': 'uppercase',
      'mobile-primary-nav-label-letter-spacing': '0.08em',
      'channel-meta-chip-background': 'var(--card)',
      'channel-meta-chip-foreground': '#f3f4ff',
      'channel-meta-chip-border': '1px solid rgba(255, 255, 255, 0.16)',
      'channel-meta-chip-shadow': 'none',
      'channel-meta-chip-icon': 'var(--muted-foreground)',
      'rating-chip-border': '1px solid rgba(255, 255, 255, 0.16)',
      'nav-item-transform': 'translate(0, 0)',
      'nav-item-transform-hover': 'translate(0, 0)',
      'transition-bouncy': 'cubic-bezier(0.4, 0, 0.2, 1)',
      'card-hover-transform': 'translateY(-2px)',
      'nav-item-text-selected': '#F3F4FF',
    },
    dark: {
      'font-body': "'IBM Plex Sans'",
      'font-display': "'Space Grotesk'",
      background: '240 10% 3.9%',
      foreground: '0 0% 98%',
      card: '240 10% 3.9%',
      'card-foreground': '0 0% 98%',
      popover: '240 10% 3.9%',
      'popover-foreground': '0 0% 98%',
      primary: '235 56% 59%',
      'primary-foreground': '0 0% 100%',
      secondary: '142 72% 45%',
      'secondary-foreground': '0 0% 10%',
      muted: '240 3.7% 15.9%',
      'muted-foreground': '240 5% 64.9%',
      accent: '38 92% 55%',
      'accent-foreground': '0 0% 10%',
      destructive: '0 72% 50%',
      'destructive-foreground': '0 0% 98%',
      'success': '142 60% 45%',
      'success-foreground': '0 0% 98%',
      'warning': '38 90% 55%',
      'warning-foreground': '0 0% 10%',
      'info': '200 85% 55%',
      'info-foreground': '0 0% 10%',
      border: '240 3.7% 15.9%',
      'border-strong': '240 5% 30%',
      input: '240 3.7% 15.9%',
      'input-border': '240 3.7% 15.9%',
      'input-border-hover': '235 56% 59%',
      ring: '235 56% 59%',
      radius: '0.125rem', // 2px
      'radius-ui': '2px',
      'radius-input': '2px',
      'radius-thumb': '2px',
      'border-weight': '1px',
      'nav-radius': '0px',
      'action-bar-gap': '6px',
      'action-bar-gap-compact': '6px',
      'action-bar-padding-y': '8px',
      'action-bar-padding-x': '0px',
      'action-bar-foreground': '0 0% 98%',
      'action-bar-border': '240 3.7% 15.9%',
      'action-bar-hover-border': '240 5% 30%',
      'action-bar-disabled-foreground': '240 5% 64.9%',
      'snackbar-surface-background': 'rgba(17, 24, 39, 0.97)',
      'snackbar-surface-border': 'rgba(255, 255, 255, 0.16)',
      'snackbar-surface-foreground': '#ffffff',
      'overlay-backdrop-background': 'rgba(0, 0, 0, 0.5)',
      'overlay-backdrop-background-strong': 'rgba(0, 0, 0, 0.6)',
      'overlay-backdrop-filter': 'blur(4px)',
      'media-placeholder-background': '#111827',
      'media-placeholder-border': '1px solid rgba(107, 114, 128, 0.4)',
      'media-overlay-foreground': '#ffffff',
      'media-overlay-background': 'rgba(0, 0, 0, 0.6)',
      'media-overlay-background-strong': 'rgba(0, 0, 0, 0.8)',
      'media-overlay-danger-background': 'rgba(211, 47, 47, 0.95)',
      'media-overlay-selection-background': 'rgba(25, 118, 210, 0.2)',
      'media-overlay-delete-selection-background': 'rgba(220, 38, 38, 0.2)',
      'media-overlay-delete-indicator-background': 'rgba(220, 38, 38, 0.8)',
      'media-overlay-ignore-button-background': 'rgba(0, 0, 0, 0.6)',
      'media-overlay-text-shadow': 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5))',
      'video-modal-media-background': 'var(--card)',
      'video-modal-media-border': '1px solid rgba(255, 255, 255, 0.16)',
      'video-modal-media-radius': 'var(--radius-ui)',
      'video-modal-media-max-height-mobile': '42vh',
      'video-modal-media-max-height-desktop': '56vh',
      'video-modal-media-overlay-gradient': 'linear-gradient(to bottom, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.52) 100%)',
      'video-modal-overlay-action-background': 'rgba(0, 0, 0, 0.78)',
      'video-modal-overlay-action-foreground': '#ffffff',
      'video-modal-overlay-action-size': '72px',
      'video-modal-overlay-action-icon-size': '48px',
      'video-modal-overlay-download-size': '90px',
      'video-modal-overlay-download-icon-size': '50px',
      'video-modal-overlay-corner-background': 'rgba(0, 0, 0, 0.64)',
      'video-modal-overlay-corner-foreground': '#ffffff',
      'video-modal-action-row-gap': '8px',
      'video-modal-action-row-padding-y': '2px',
      'video-modal-action-control-height': '28px',
      'video-modal-action-control-min-width': '28px',
      'video-modal-action-control-padding-x': '8px',
      'video-modal-content-gap': '10px',
      'video-modal-content-padding-mobile': '8px',
      'video-modal-content-padding-desktop': '12px',
      'video-modal-action-divider': 'rgba(255, 255, 255, 0.18)',
      'video-modal-section-gap': '12px',
      'video-modal-delete-outline': 'var(--destructive)',
      'video-modal-delete-foreground': 'var(--destructive)',
      'video-modal-link-color': 'hsl(var(--primary))',
      'video-modal-info-bubble-background': 'rgba(18, 18, 20, 0.98)',
      'video-modal-info-bubble-foreground': 'var(--foreground)',
      'video-modal-info-bubble-border': '1px solid rgba(255, 255, 255, 0.12)',
      'video-modal-info-bubble-shadow': '0 8px 24px rgba(0, 0, 0, 0.4)',
      'auth-splash-background': 'linear-gradient(135deg, #09090b 0%, #1a1a1f 100%)',
      'auth-surface-background': 'rgba(18, 18, 20, 0.95)',
      'auth-surface-border': '1px solid rgba(255, 255, 255, 0.1)',
      'auth-surface-shadow': '0 8px 32px rgba(0, 0, 0, 0.5)',
      'auth-surface-backdrop-filter': 'blur(20px)',
      'auth-surface-transform': 'none',
      'auth-surface-padding': '32px',
      'auth-title-font-weight': '700',
      'auth-title-font-size': '2.5rem',
      'auth-title-letter-spacing': '0.02em',
      'auth-title-text-shadow': '0 2px 10px rgba(102, 126, 234, 0.5)',
      'auth-subtitle-font-size': '0.9rem',
      'auth-button-text-transform': 'uppercase',
      'auth-button-letter-spacing': '0.1em',
      'linear-decor-blob-primary': 'radial-gradient(circle, rgba(94, 106, 210, 0.55) 0%, transparent 70%)',
      'linear-decor-blob-secondary': 'radial-gradient(circle, rgba(244, 114, 182, 0.4) 0%, transparent 70%)',
      'linear-decor-top-rail': 'linear-gradient(90deg, transparent, rgba(94, 106, 210, 0.5), transparent)',
      'fab-primary-bg': 'var(--primary)',
      'fab-primary-fg': 'var(--primary-foreground)',
      'fab-primary-hover-bg': 'var(--primary)',
      'fab-primary-hover-fg': 'var(--primary-foreground)',
      'fab-secondary-bg': 'var(--secondary)',
      'fab-secondary-fg': 'var(--secondary-foreground)',
      'fab-secondary-hover-bg': 'var(--secondary)',
      'fab-secondary-hover-fg': 'var(--secondary-foreground)',
      'fab-error-bg': 'var(--destructive)',
      'fab-error-fg': 'var(--destructive-foreground)',
      'fab-error-hover-bg': 'var(--destructive)',
      'fab-error-hover-fg': 'var(--destructive-foreground)',
      'selection-fab-size': '58px',
      'selection-fab-radius': '0px',
      'selection-fab-border': '1px solid rgba(255, 255, 255, 0.12)',
      'selection-fab-shadow': '0 0 0 1px rgba(255, 255, 255, 0.1), 0 18px 38px rgba(0, 0, 0, 0.42)',
      'selection-fab-shadow-hover': '0 0 0 1px rgba(94, 106, 210, 0.35), 0 24px 48px rgba(0, 0, 0, 0.5)',
      'selection-fab-shadow-active': '0 0 0 1px rgba(94, 106, 210, 0.45), 0 14px 24px rgba(0, 0, 0, 0.34)',
      'selection-fab-icon-size': '22px',
      'selection-fab-badge-background': 'rgba(18, 18, 20, 0.96)',
      'selection-fab-badge-foreground': '#ffffff',
      'selection-fab-badge-border': '1px solid rgba(255, 255, 255, 0.14)',
      'selection-fab-badge-shadow': '0 8px 20px rgba(0, 0, 0, 0.32)',
      'selection-fab-badge-radius': '999px',
      'audio-chip-radius': '0px',
      'audio-chip-bg': 'transparent',
      'audio-chip-border': 'rgba(255, 255, 255, 0.4)',
      'audio-chip-foreground': 'var(--foreground)',
      'audio-chip-icon': 'var(--foreground)',
      'audio-chip-shadow': 'none',
      'audio-chip-shadow-hover': 'none',
      'video-chip-radius': '0px',
      'video-chip-bg': 'transparent',
      'video-chip-border': 'rgba(255, 255, 255, 0.25)',
      'video-chip-foreground': 'var(--foreground)',
      'video-chip-icon': 'var(--foreground)',
      'video-chip-shadow': 'none',
      'video-chip-shadow-hover': 'none',
      'audio-control-radius': '0px',
      'audio-control-border': 'rgba(255, 255, 255, 0.4)',
      'audio-control-bg': 'rgba(0, 0, 0, 0.6)',
      'audio-control-foreground': 'var(--foreground)',
      'audio-control-shadow': '0 0 0 1px rgba(255, 255, 255, 0.2)',
      'audio-control-shadow-focus': '0 0 0 1px rgba(255, 255, 255, 0.35)',
      /* Chip & rating shadows */
      'chip-shadow': 'none',
      'chip-shadow-hover': '0 8px 24px rgba(0,0,0,0.35)',
      'rating-chip-shadow': 'none',
      'rating-chip-shadow-hover': '0 8px 24px rgba(0,0,0,0.35)',
      'status-chip-max-width': '140px',
      'rating-chip-max-width': '120px',
      'nav-hover-style': 'flat-highlight',
      'shadow-soft': '0 8px 32px rgba(0, 0, 0, 0.4)',
      'shadow-hard': '0 12px 48px rgba(0, 0, 0, 0.6)',
      'shadow-hard-hover': '0 16px 64px rgba(0, 0, 0, 0.8)',
      'transition-smooth': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      'nav-border': '1px solid rgba(255, 255, 255, 0.08)',
      'nav-shadow': '0 12px 40px rgba(0, 0, 0, 0.45)',
      'nav-item-border': '1px solid rgba(255, 255, 255, 0.08)',
      'nav-item-border-selected': '1px solid rgba(94, 106, 210, 0.6)',
      'nav-item-border-hover': '1px solid rgba(255, 255, 255, 0.08)',
      'nav-item-bg': 'rgba(255, 255, 255, 0.02)',
      'nav-item-bg-selected': 'rgba(94, 106, 210, 0.2)',
      'nav-item-bg-hover': 'rgba(255, 255, 255, 0.06)',
      'nav-item-shadow': 'none',
      'nav-item-shadow-selected': '0 8px 24px rgba(0, 0, 0, 0.35)',
      'nav-item-shadow-hover': '0 8px 24px rgba(0, 0, 0, 0.25)',
      'header-nav-active-color': 'hsl(var(--primary))',
      'header-nav-default-color': 'var(--muted-foreground)',
      'header-subnav-active-color': 'hsl(var(--primary))',
      'header-update-indicator-width': '32px',
      'header-update-indicator-height': '32px',
      'header-update-indicator-radius': '6px',
      'header-update-indicator-foreground': 'var(--warning)',
      'header-update-indicator-background': 'transparent',
      'header-update-indicator-border': '1px solid rgba(255, 255, 255, 0.2)',
      'header-update-indicator-shadow': 'none',
      'mobile-subnav-surface-background': '#050506',
      'mobile-subnav-surface-border-top': '1px solid rgba(255, 255, 255, 0.1)',
      'mobile-subnav-surface-radius': 'var(--radius-ui)',
      'mobile-subnav-surface-margin-bottom': '0',
      'mobile-subnav-surface-padding-bottom': '8px',
      'mobile-subnav-item-border': '1px solid rgba(255, 255, 255, 0.12)',
      'mobile-subnav-item-border-selected': '1px solid rgba(94, 106, 210, 0.7)',
      'mobile-subnav-item-radius': 'var(--radius-ui)',
      'mobile-subnav-item-text-transform': 'uppercase',
      'mobile-subnav-item-letter-spacing': '0.08em',
      'mobile-primary-nav-surface-background': '#050506',
      'mobile-primary-nav-surface-border-top': '1px solid rgba(255, 255, 255, 0.1)',
      'mobile-primary-nav-surface-radius': '0',
      'mobile-primary-nav-surface-shadow': '0 -12px 30px rgba(0, 0, 0, 0.45)',
      'mobile-primary-nav-active-color': '#ffffff',
      'mobile-primary-nav-active-background': 'rgba(94, 106, 210, 0.16)',
      'mobile-primary-nav-label-font-size': '0.62rem',
      'mobile-primary-nav-label-text-transform': 'uppercase',
      'mobile-primary-nav-label-letter-spacing': '0.08em',
      'channel-meta-chip-background': 'var(--card)',
      'channel-meta-chip-foreground': '#f3f4ff',
      'channel-meta-chip-border': '1px solid rgba(255, 255, 255, 0.16)',
      'channel-meta-chip-shadow': 'none',
      'channel-meta-chip-icon': 'var(--muted-foreground)',
      'rating-chip-border': '1px solid rgba(255, 255, 255, 0.16)',
      'nav-item-transform': 'translate(0, 0)',
      'nav-item-transform-hover': 'translate(0, 0)',
      'transition-bouncy': 'cubic-bezier(0.4, 0, 0.2, 1)',
      'card-hover-transform': 'translateY(-2px)',
      'nav-item-text-selected': '#F3F4FF',
    },
  },
};
